// src/services/baseService.ts - ATUALIZADO
import { supabase } from "../lib/supabase/client";
import type { Database } from "../lib/supabase/database.types";

type TableName = keyof Database["public"]["Tables"];

// Tipos utilitários para inferência automática
type TableRow<T extends TableName> = Database["public"]["Tables"][T]["Row"];
type TableInsert<T extends TableName> =
  Database["public"]["Tables"][T]["Insert"];
type TableUpdate<T extends TableName> =
  Database["public"]["Tables"][T]["Update"];

export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  totalCount?: number;
  message?: string;
  originalError?: any;
}

/**
 * Serviço base com operações CRUD genéricas
 */
export class BaseService<T extends TableName> {
  private tableName: T;

  constructor(tableName: T) {
    this.tableName = tableName;
  }

  /**
   * Busca todos os registros com paginação
   */
  async findAll(
    options: {
      columns?: string;
      filters?: Record<string, any>;
      orderBy?: { column: string; ascending?: boolean };
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<ServiceResponse<TableRow<T>[]>> {
    try {
      const table = supabase.from(this.tableName as any) as any;
      let query = table.select(options.columns || "*", { count: "exact" } as any);

      // Aplica filtros
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            query = query.eq(key as any, value);
          }
        });
      }

      // Aplica ordenação
      if (options.orderBy) {
        query = query.order(options.orderBy.column as any, {
          ascending: options.orderBy.ascending ?? true,
        });
      }

      // Aplica paginação
      if (options.page && options.pageSize) {
        const from = (options.page - 1) * options.pageSize;
        const to = from + options.pageSize - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        success: true,
        data: data as TableRow<T>[],
        totalCount: count || 0,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Busca registro por ID
   */
  async findById(
    id: string,
    columns?: string
  ): Promise<ServiceResponse<TableRow<T>>> {
    try {
      const table = supabase.from(this.tableName as any) as any;
      const { data, error } = await table
        .select(columns || "*")
        .eq("id" as any, id)
        .single();

      if (error) throw error;

      return {
        success: true,
        data: data as TableRow<T>,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Cria novo registro
   */
  async create(payload: TableInsert<T>): Promise<ServiceResponse<TableRow<T>>> {
    try {
      const table = supabase.from(this.tableName as any) as any;
      const { data, error } = await table
        .insert(payload as any)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: data as TableRow<T>,
        message: "Registro criado com sucesso",
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Atualiza registro
   */
  async update(
    id: string,
    payload: TableUpdate<T>
  ): Promise<ServiceResponse<TableRow<T>>> {
    try {
      // Adiciona updated_at automaticamente
      const updateData = {
        ...payload,
        updated_at: new Date().toISOString(),
      } as any;

      const table = supabase.from(this.tableName as any) as any;
      const { data, error } = await table
        .update(updateData)
        .eq("id" as any, id)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: data as TableRow<T>,
        message: "Registro atualizado com sucesso",
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Exclui registro (soft delete se possível)
   */
  async delete(id: string, softDelete = true): Promise<ServiceResponse<void>> {
    try {
      const table = supabase.from(this.tableName as any) as any;
      if (softDelete) {
        // Tenta soft delete primeiro
        const { data, error } = await table
          .update({
            is_active: false,
            updated_at: new Date().toISOString(),
          } as any)
          .eq("id" as any, id)
          .select()
          .single();

        if (!error) {
          return {
            success: true,
            data: data as any,
            message: "Registro desativado com sucesso",
          };
        }
      }

      // Hard delete como fallback
      const { error } = await table.delete().eq("id" as any, id);

      if (error) throw error;

      return {
        success: true,
        message: "Registro excluído com sucesso",
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Tratamento centralizado de erros
   */
  private handleError(error: any): ServiceResponse<never> {
    console.error(`Erro no serviço ${this.tableName}:`, error);

    const errorMap: Record<string, string> = {
      "23505": "Registro duplicado",
      "23503": "Registro referenciado não existe",
      "42501": "Permissão negada",
      "42P01": "Tabela não existe",
      "Network Error": "Erro de conexão com o servidor",
    };

    const errorMessage =
      (error && errorMap[error.code]) ||
      (error && error.message) ||
      "Erro desconhecido no banco de dados";

    const isDevelopment = import.meta.env.DEV;

    return {
      success: false,
      error: errorMessage,
      originalError: isDevelopment ? error : undefined,
    };
  }
}